import { Injectable } from '@angular/core';
import { ValidatorFn, Validators } from '@angular/forms';
import { isFormGroup, predicateValidator } from '@fmnts/common/forms';
import { getUnique } from '@fmnts/core/collection';
import { EMPTY, map, OperatorFunction } from 'rxjs';
import { ConditionalService } from '../condition/conditional.service';
import { ConditionDefinition } from '../condition/conditions';
import { ConditionalValidatorFn } from './validation.types';

/**
 * @param fn Validator function to check
 *
 * @returns
 * `true`, if `fn` is a `ConditionalValidatorFn`
 */
export function isConditionalValidatorFn(
  fn: Record<string, any>,
): fn is ConditionalValidatorFn {
  return 'conditions' in fn && Array.isArray(fn['conditions']);
}

/**
 * Service for creating conditional form validator functions
 */
@Injectable({
  providedIn: 'root',
})
export class ConditionalValidationService {
  constructor(private conditionalService: ConditionalService) {}

  /**
   * Composes a validator function with a given `condition` from another
   * `validator` function
   *
   * @param condition The condition that should be fulfilled
   * @param validator Validator function to be wrapped
   *
   * @returns
   * A new validator function that will call the given `validator`
   * if the given `condition` is fulfilled and if control with which
   * the validator is called is inside a `FormGroup`.
   *
   * @example
   * control.setValue(0);
   * otherControl.setValue('this-value');
   * const condition = { key: 'other-field', equals: 'this-value' };
   *
   * const validator = service.withCondition(condition, Validators.min(2));
   * validator(control); // => { min: { min: 2, actual: 0 }}
   *
   * @example
   * control.setValue(0);
   * otherControl.setValue('some-other-value');
   * const condition = { key: 'other-field', equals: 'this-value' };
   *
   * const validator = service.withCondition(condition, Validators.min(2));
   * validator(control); // => null
   */
  public withCondition(
    condition: ConditionDefinition,
    validator: ValidatorFn,
  ): ConditionalValidatorFn {
    const conditionalValidator: ConditionalValidatorFn = predicateValidator(
      validator,
      (control) => {
        const { root } = control;
        return isFormGroup(root)
          ? (this.conditionalService.checkGroup(root, condition) ?? false)
          : false;
      },
    );

    conditionalValidator.conditions = [condition];

    return conditionalValidator;
  }

  /**
   * Composes a new validator function from the given conditional `validators`
   *
   * @param validators Validator functions that should be composed
   *
   * @returns
   * A new validator function from the given validators. If no validators
   * were given, `null` will be returned.
   *
   * @example
   * const validator1 = service.withCondition(...);
   * const validator2 = service.withCondition(...);
   *
   * const validator = service.compose([validator1, validator2]);
   * validator(control); // Will call validators
   */
  public compose(
    validators: ConditionalValidatorFn[],
  ): ConditionalValidatorFn | null {
    const finalValidator = Validators.compose(
      validators,
    ) as ConditionalValidatorFn;

    // Set conditions
    if (finalValidator) {
      const conditions = validators.flatMap((v) =>
        getUnique(v.conditions ?? []),
      );
      finalValidator.conditions =
        conditions.length > 0 ? conditions : undefined;
    }

    return finalValidator;
  }

  /**
   * Observes a conditional validator function for updates
   *
   * @param validators Validator functions
   *
   * @returns
   * Observable that emits with the given validator function
   * if there might be an update needed
   */
  public needsUpdate$(
    validators: ConditionalValidatorFn[],
  ): OperatorFunction<any, ConditionalValidatorFn[]> {
    // Extract conditions from all validators
    const conditions = validators.flatMap((v) => v.conditions ?? []);

    // When there are no conditions, complete
    if (!conditions || conditions.length === 0) {
      return () => EMPTY;
    }

    return (obs$) =>
      obs$.pipe(
        this.conditionalService.fulfills$({ or: conditions }),
        map(() => validators),
      );
  }
}
